// Breaks are kept per day of the week (0 is Sunday) as [start, end] pairs: "HH:mm" since they have minutes, and whole
// hours (numbers) before that, which still read as they were.

/** A break time as minutes from midnight */
export const breakMinutes = value => {
  if (typeof value === 'string' && value.includes(':')) {
    const [hours, minutes] = value.split(':').map(Number)
    return (hours || 0) * 60 + (minutes || 0)
  }
  return (Number(value) || 0) * 60
}

/** A minutes-from-midnight time the way a break keeps it */
export const minutesToBreakTime = minutes =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

/** A branch's (or user's) breaks with the company's, which apply to every branch, day by day */
export const withCompanyBreaks = (breaks, companyBreaks) =>
  new Array(7).fill(0).map((_, day) => [...(breaks?.[day] || []), ...(companyBreaks?.[day] || [])])

/** A day's breaks as [start, end] minutes from midnight, leaving out the empty ones */
const timedBreaks = dayBreaks =>
  (dayBreaks || [])
    .filter(b => Array.isArray(b) && b.length > 1)
    .map(b => [breakMinutes(b[0]), breakMinutes(b[1])])
    .filter(([start, end]) => end !== start)

/**
 * A day's breaks as [start, end] minutes from midnight, leaving out the empty ones. A break that ends before it starts
 * runs past midnight: its own day keeps it until midnight, and the day after keeps the rest from midnight.
 */
export const breaksForDay = (breaks, day) => [
  ...timedBreaks(breaks?.[day]).map(([start, end]) => [start, end > start ? end : 24 * 60]),
  ...timedBreaks(breaks?.[(day + 6) % 7])
    .filter(([start, end]) => end < start)
    .map(([, end]) => [0, end])
]

/** A span's end in minutes from its day's midnight: one that ends before it starts ends the day after (00:00 is 1440) */
export const spanEndMinutes = (start, end) => (end < start ? end + 24 * 60 : end)

/** Whether a time, or a span when `end` is given (minutes from midnight), falls in a break */
export const overlapsBreak = (dayBreaks, start, end) =>
  dayBreaks.some(([breakStart, breakEnd]) =>
    end === undefined ? start >= breakStart && start < breakEnd : start < breakEnd && spanEndMinutes(start, end) > breakStart
  )

/** Minutes from midnight of a moment, dayjs or Date */
export const minutesOfDay = date => {
  const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date)
  return date.hour ? date.hour() * 60 + date.minute() : d.getHours() * 60 + d.getMinutes()
}
